import { useCallback, useRef, useState } from "react";

export interface SortConfig {
  fieldName: string;
  order: "asc" | "desc";
}

export type SearchFn<T> = (
  query: string,
  options: { sort: SortConfig; cursor?: unknown }
) => Promise<T[]>;

export interface UseCursorPaginatedSearchParams<T> {
  searchFn: SearchFn<T>;
  sort: SortConfig;
  pageSize?: number;
  initialLoading?: boolean;
}

export interface UseCursorPaginatedSearchReturn<T> {
  results: T[];
  loading: boolean;
  hasMore: boolean;
  handleFetch: (query: string) => Promise<void>;
  handleNextPage: () => Promise<void>;
  reset: () => void;
}

export function useCursorPaginatedSearch<T extends Record<string, unknown>>({
  searchFn,
  sort,
  pageSize,
  initialLoading
}: UseCursorPaginatedSearchParams<T>): UseCursorPaginatedSearchReturn<T> {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(initialLoading ?? false);
  const [hasMore, setHasMore] = useState(false);

  const resultsRef = useRef<T[]>([]);
  const searchCountRef = useRef(0);
  const fetchingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const queryRef = useRef("");

  // Sync params to refs so callbacks stay stable
  const searchFnRef = useRef(searchFn);
  searchFnRef.current = searchFn;
  const sortRef = useRef(sort);
  sortRef.current = sort;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const handleFetch = useCallback(async (query: string) => {
    const currentSearch = ++searchCountRef.current;
    queryRef.current = query;
    resultsRef.current = [];
    fetchingRef.current = false;
    hasMoreRef.current = false;

    setLoading(true);
    try {
      const page = await searchFnRef.current(query, { sort: sortRef.current });

      if (currentSearch !== searchCountRef.current) return;

      resultsRef.current = page;
      const ps = pageSizeRef.current;
      const more = ps != null ? page.length >= ps : page.length > 0;
      hasMoreRef.current = more;

      setResults(page);
      setHasMore(more);
    } catch (error) {
      console.error("Search fetch failed:", error);
      if (currentSearch === searchCountRef.current) {
        resultsRef.current = [];
        hasMoreRef.current = false;
        setResults([]);
        setHasMore(false);
      }
    } finally {
      if (currentSearch === searchCountRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const handleNextPage = useCallback(async () => {
    if (fetchingRef.current || !hasMoreRef.current) return;

    const currentResults = resultsRef.current;
    if (currentResults.length === 0) return;

    const lastResult = currentResults[currentResults.length - 1];
    const cursor = lastResult[sortRef.current.fieldName];
    if (cursor == null) return;

    const currentSearch = searchCountRef.current;
    fetchingRef.current = true;
    setLoading(true);

    try {
      const page = await searchFnRef.current(queryRef.current, {
        sort: sortRef.current,
        cursor
      });

      if (currentSearch !== searchCountRef.current) return;

      const ps = pageSizeRef.current;
      const more = ps != null ? page.length >= ps : page.length > 0;
      hasMoreRef.current = more;
      resultsRef.current = [...resultsRef.current, ...page];

      setResults(prev => [...prev, ...page]);
      setHasMore(more);
    } catch (error) {
      console.error("Search next page failed:", error);
    } finally {
      fetchingRef.current = false;
      if (currentSearch === searchCountRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    searchCountRef.current++;
    fetchingRef.current = false;
    hasMoreRef.current = false;
    queryRef.current = "";
    resultsRef.current = [];
    setResults([]);
    setLoading(false);
    setHasMore(false);
  }, []);

  return { results, loading, hasMore, handleFetch, handleNextPage, reset };
}
